// ============================================================
// User timezone preference
// ============================================================
// Per-admin timezone, persisted server-side via the shared admin
// preferences API. Use the `formatDate` / `formatDateTime` /
// `formatTime` / `formatRelative` helpers returned by the hook to
// render any date in the user's chosen zone — never call
// `toLocaleString()` or `date-fns format()` directly so the whole
// console renders consistently.
//
//   const { formatDateTime } = useUserTimezone();
//   <span>{formatDateTime(device.lastSeen)}</span>
//
// "Auto (browser)" follows the OS / browser zone live; explicit IANA
// zones (e.g. "America/New_York") render the same time everywhere.
// ============================================================

import { useCallback, useMemo } from "react";
import { useUserPreference } from "./useUserPreference";

export const TIMEZONE_PREF_KEY = "ui.timezone";
export const TIMEZONE_LOCAL_STORAGE_KEY = "uiTimezone";

/** Sentinel meaning "follow the browser/OS zone". */
export const AUTO_TIMEZONE = "auto" as const;
export type TimezoneValue = typeof AUTO_TIMEZONE | string;

export interface TimezoneOption {
  /** Stored value (`"auto"` or an IANA zone). */
  value: TimezoneValue;
  /** Human label shown in dropdowns. */
  label: string;
}

/**
 * Curated short list of common business zones. Backed by IANA zone IDs
 * so DST transitions are handled automatically by `Intl.DateTimeFormat`.
 * Order is intentional: Auto first, then UTC, then west → east.
 */
export const TIMEZONE_OPTIONS: TimezoneOption[] = [
  { value: AUTO_TIMEZONE, label: "Auto (browser)" },
  { value: "UTC", label: "UTC" },
  { value: "America/Los_Angeles", label: "Pacific Time — Los Angeles" },
  { value: "America/Denver", label: "Mountain Time — Denver" },
  { value: "America/Chicago", label: "Central Time — Chicago" },
  { value: "America/New_York", label: "Eastern Time — New York" },
  { value: "America/Sao_Paulo", label: "Brasília — São Paulo" },
  { value: "Europe/London", label: "London" },
  { value: "Europe/Paris", label: "Paris / Berlin / Madrid" },
  { value: "Europe/Athens", label: "Athens / Helsinki" },
  { value: "Africa/Johannesburg", label: "Johannesburg" },
  { value: "Asia/Dubai", label: "Dubai" },
  { value: "Asia/Kolkata", label: "India — Kolkata" },
  { value: "Asia/Singapore", label: "Singapore" },
  { value: "Asia/Tokyo", label: "Tokyo" },
  { value: "Australia/Sydney", label: "Sydney" },
];

const SUPPORTED_VALUES = new Set<string>(TIMEZONE_OPTIONS.map((o) => o.value));

function isValidTimezone(v: unknown): v is TimezoneValue {
  return typeof v === "string" && SUPPORTED_VALUES.has(v);
}

function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

function toDate(input: Date | string | number | null | undefined): Date | null {
  if (input === null || input === undefined || input === "") return null;
  const d = input instanceof Date ? input : new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Per-user timezone preference + memoized formatters. All console date
 * displays should go through these helpers so the user's chosen zone is
 * applied uniformly.
 */
export function useUserTimezone() {
  const { value, setValue, isSaving } = useUserPreference<TimezoneValue>({
    key: TIMEZONE_PREF_KEY,
    defaultValue: AUTO_TIMEZONE,
    validate: isValidTimezone,
    localStorageKey: TIMEZONE_LOCAL_STORAGE_KEY,
    errorMessage: "Couldn't save your timezone. Reverted to the previous value.",
  });

  /** The actual IANA zone passed to `Intl.DateTimeFormat`. */
  const resolvedTimezone = useMemo(
    () => (value === AUTO_TIMEZONE ? getBrowserTimezone() : value),
    [value]
  );

  const baseOptions: Intl.DateTimeFormatOptions = useMemo(
    () => ({ timeZone: resolvedTimezone }),
    [resolvedTimezone]
  );

  /** Date only, e.g. "Apr 18, 2026". */
  const formatDate = useCallback(
    (
      input: Date | string | number | null | undefined,
      overrides?: Intl.DateTimeFormatOptions
    ) => {
      const d = toDate(input);
      if (!d) return "—";
      return new Intl.DateTimeFormat(undefined, {
        ...baseOptions,
        year: "numeric",
        month: "short",
        day: "numeric",
        ...overrides,
      }).format(d);
    },
    [baseOptions]
  );

  /** Date + time, e.g. "Apr 18, 2026, 5:23 PM". */
  const formatDateTime = useCallback(
    (
      input: Date | string | number | null | undefined,
      overrides?: Intl.DateTimeFormatOptions
    ) => {
      const d = toDate(input);
      if (!d) return "—";
      return new Intl.DateTimeFormat(undefined, {
        ...baseOptions,
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        ...overrides,
      }).format(d);
    },
    [baseOptions]
  );

  /** Time only, e.g. "5:23 PM". */
  const formatTime = useCallback(
    (
      input: Date | string | number | null | undefined,
      overrides?: Intl.DateTimeFormatOptions
    ) => {
      const d = toDate(input);
      if (!d) return "—";
      return new Intl.DateTimeFormat(undefined, {
        ...baseOptions,
        hour: "numeric",
        minute: "2-digit",
        ...overrides,
      }).format(d);
    },
    [baseOptions]
  );

  /**
   * Relative phrases like "5 minutes ago" or "in 2 days". Zone-agnostic
   * (deltas don't depend on zone), but exposed here so all date helpers
   * live in one hook.
   */
  const formatRelative = useCallback(
    (input: Date | string | number | null | undefined): string => {
      const d = toDate(input);
      if (!d) return "";
      const diffMs = d.getTime() - Date.now();
      const absMin = Math.abs(diffMs) / 60_000;
      const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
      if (absMin < 1) return "just now";
      if (absMin < 60) return rtf.format(Math.round(diffMs / 60_000), "minute");
      const absHr = absMin / 60;
      if (absHr < 24) return rtf.format(Math.round(diffMs / 3_600_000), "hour");
      const absDay = absHr / 24;
      if (absDay < 30) return rtf.format(Math.round(diffMs / 86_400_000), "day");
      const absMonth = absDay / 30;
      if (absMonth < 12) return rtf.format(Math.round(absMonth) * Math.sign(diffMs), "month");
      return rtf.format(Math.round(absDay / 365) * Math.sign(diffMs), "year");
    },
    []
  );

  return {
    timezone: value,
    resolvedTimezone,
    setTimezone: setValue,
    isSaving,
    formatDate,
    formatDateTime,
    formatTime,
    formatRelative,
  };
}
